import { useEffect, useRef, useState, useContext } from 'react';
import { Dialog, DialogContent } from '@/components/ui/SossietyDialog';
import { SossietyDialogHeader, SossietyDialogClose } from '@/components/ui/SossietyDialogHeader';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/contexts/I18nContext';
import { AuthContext } from '@/contexts/AuthContext';
import { isIOSDevice, isIOSSafari, isStandalonePWA } from '@/utils/platform';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
};

const MAX_SHOW_COUNT = 3;
const PWA_SHOW_COUNT_KEY = 'pwaInstallShowCount';
const PWA_DISMISSED_KEY = 'pwaInstallDismissed';

export const PWAInstallDialog: React.FC = () => {
  const { t } = useI18n();
  const authContext = useContext(AuthContext);

  if (!authContext) return null;

  const { user, loading } = authContext;
  const [open, setOpen] = useState(false);
  const [showIosTip, setShowIosTip] = useState(false);
  const deferredPromptRef = useRef<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    if (loading || !user) return;
    if (isStandalonePWA()) {
      localStorage.setItem(PWA_DISMISSED_KEY, '1');
      return;
    }

    if (localStorage.getItem(PWA_DISMISSED_KEY) === '1') return;

    const showCount = parseInt(localStorage.getItem(PWA_SHOW_COUNT_KEY) || '0', 10);
    if (showCount >= MAX_SHOW_COUNT) {
      localStorage.setItem(PWA_DISMISSED_KEY, '1');
      return;
    }

    if ((isIOSDevice() || isIOSSafari()) && !isStandalonePWA()) {
      setShowIosTip(true);
      setOpen(true);
      localStorage.setItem(PWA_SHOW_COUNT_KEY, String(showCount + 1));
    }

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      deferredPromptRef.current = e as BeforeInstallPromptEvent;
      if (showCount < MAX_SHOW_COUNT) {
        setOpen(true);
        localStorage.setItem(PWA_SHOW_COUNT_KEY, String(showCount + 1));
      }
    };

    const handleAppInstalled = () => {
      deferredPromptRef.current = null;
      setOpen(false);
      localStorage.setItem(PWA_DISMISSED_KEY, '1');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [user, loading]);

  const installPwa = async () => {
    const deferred = deferredPromptRef.current;
    if (!deferred) {
      handleDismiss();
      return;
    }
    await deferred.prompt();
    deferredPromptRef.current = null;
    setOpen(false);
    localStorage.setItem(PWA_DISMISSED_KEY, '1');
  };

  const handleDismiss = () => {
    const showCount = parseInt(localStorage.getItem(PWA_SHOW_COUNT_KEY) || '0', 10);
    if (showCount >= MAX_SHOW_COUNT) localStorage.setItem(PWA_DISMISSED_KEY, '1');
    setOpen(false);
  };

  if (loading || !user) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(val) => { if (!val) handleDismiss(); else setOpen(val); }}
    >
      <DialogContent
        className="w-[80vw] max-w-md bg-white rounded-2xl max-h-[80vh] overflow-hidden flex flex-col"
        showCloseButton={false}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <div className="relative mb-4 flex-shrink-0">
          <div className="flex items-center justify-between relative pt-4 pb-4 px-2">
            <div className="flex-1" />
            <h2 className="text-xl font-semibold text-black text-center leading-none tracking-normal flex-1">
              {t('pwa.install_title')}
            </h2>
            <div className="flex-1 flex justify-end pr-8">
              <SossietyDialogClose onClick={handleDismiss} />
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-2">
          <div className="flex flex-col gap-3 py-4">
            <p className="text-sm text-gray-600">
              {t('pwa.install_description')}
            </p>

            {showIosTip && (
              <ol className="space-y-2 text-sm text-gray-700 mt-1">
                <li className="flex gap-2">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-sossiety-red/10 text-sossiety-red text-xs font-bold flex items-center justify-center">1</span>
                  <span>{t('pwa.ios_push_step_1')}</span>
                </li>
                <li className="flex gap-2">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-sossiety-red/10 text-sossiety-red text-xs font-bold flex items-center justify-center">2</span>
                  <span>{t('pwa.ios_push_step_2')}</span>
                </li>
                <li className="flex gap-2">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-sossiety-red/10 text-sossiety-red text-xs font-bold flex items-center justify-center">3</span>
                  <span>{t('pwa.ios_push_step_3')}</span>
                </li>
              </ol>
            )}
          </div>
        </div>

        <div className="flex gap-3 pt-4 pb-4 flex-shrink-0">
          <Button variant="outline" onClick={handleDismiss} className="flex-1 rounded-full">
            {t('pwa.not_now')}
          </Button>
          <Button onClick={installPwa} className="flex-1 rounded-full bg-sossiety-red text-white hover:bg-sossiety-red-hover">
            {t('pwa.install_button')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
